import { makeAutoObservable, runInAction, toJS } from 'mobx';
import localforage from 'localforage';
import ServiceStore from './ServiceStore';
import { ConflictResolver } from './offline/ConflictResolver';
import type RootStore from './RootStore';

export interface OfflineMetadata {
  _isTemp?: boolean;
  _localCreatedAt?: number;
  _localUpdatedAt?: number;
  _localDeletedAt?: number;
  _deleted?: boolean;
  _syncStatus?: 'pending' | 'syncing' | 'synced' | 'error';
  _conflictData?: any;
}

export interface OfflineServiceStoreOptions {
  enableOffline?: boolean;
  conflictStrategy?: 'client' | 'server' | 'merge' | 'manual';
  cacheTTL?: number;
  syncOnReconnect?: boolean;
}

export class OfflineServiceStore extends ServiceStore {
  offlineStorage: LocalForage | null = null;
  lastSyncTime: Date | null = null;
  syncInProgress = false;
  conflictResolver: ConflictResolver<any>;
  offlineOptions: OfflineServiceStoreOptions = {
    enableOffline: true,
    conflictStrategy: 'server',
    cacheTTL: 5 * 60 * 1000, // 5 minutes
    syncOnReconnect: true
  };
  
  private syncPromise: Promise<void> | null = null;
  private pendingSync = new Set<string>();

  constructor(
    rootStore: RootStore,
    serviceName: string,
    options: any = {}
  ) {
    super(rootStore, serviceName, options);
    
    Object.assign(this.offlineOptions, options.offline || {});
    
    if (this.offlineOptions.enableOffline) {
      this.offlineStorage = localforage.createInstance({
        name: 'offlineStore',
        storeName: this.name || serviceName
      });
      
      this.conflictResolver = new ConflictResolver(this.name || serviceName);
      
      makeAutoObservable(this, { 
        rootStore: false,
        service: false,
        storage: false,
        offlineStorage: false,
        conflictResolver: false
      });
      
      this.initializeOfflineStore();
    }
  }

  private async initializeOfflineStore() {
    // Load cached data first
    await this.hydrateFromOfflineStorage();
    
    // Setup connection listener for auto-sync
    if (this.offlineOptions.syncOnReconnect && this.rootStore.connectionStore) {
      this.setupConnectionListener();
    }
  }

  private async hydrateFromOfflineStorage() {
    if (!this.offlineStorage) return;
    
    try {
      const keys = await this.offlineStorage.keys();
      const items = await Promise.all(
        keys.map(async key => {
          if (key.startsWith('_')) return null; // Skip metadata keys
          return await this.offlineStorage!.getItem<any>(key);
        })
      );

      runInAction(() => {
        items.forEach(item => {
          if (item && !item._deleted) {
            const existingIndex = this.items.findIndex(
              (i: any) => i[this.idField] === item[this.idField]
            );
            if (existingIndex >= 0) {
              this.items[existingIndex] = item;
            } else {
              this.items.push(item);
            }
          }
        });
      });
    } catch (error) {
      console.error(`Failed to hydrate ${this.name} from offline storage:`, error);
    }
  }

  private setupConnectionListener() {
    if (!this.rootStore.connectionStore) return;
    
    // Listen for connection changes
    const connectionStore = this.rootStore.connectionStore;
    if (connectionStore.addConnectionListener) {
      connectionStore.addConnectionListener(async (isOnline: boolean) => {
        if (isOnline && this.offlineOptions.syncOnReconnect) {
          await this.syncWithServer();
        }
      });
    }
  }

  find = async (params: { query?: { [key: string]: unknown } } = {}) => {
    const isOffline = !navigator.onLine || 
      (this.rootStore.connectionStore?.isOffline ?? false);
    
    if (isOffline && this.offlineOptions.enableOffline) {
      return this.findOffline(params);
    }

    try {
      // Directly call the service since we can't call super on arrow functions
      this.isFindPending = true;
      let response: any = null;

      try {
        if (this.pagination.enabled) {
          if (!params.query)
            Object.assign(params, {
              query: {},
            });
          if (params.query) {
            Object.assign(params.query, {
              $limit: this.pagination.pageSize,
              $skip: this.pagination.pageSize * this.pagination.currentPage,
            });
          }
        }
        
        response = await this.service.find(params);
        
        if (response?.data && response?.total !== undefined) {
          runInAction(() => {
            this.items = response.data;
            this.setPageTotal(response.total);
          });
        } else if (Array.isArray(response)) {
          runInAction(() => {
            this.items = response;
          });
        }
        
        runInAction(() => {
          this.errorOnFind = null;
        });
      } catch (error: any) {
        runInAction(() => {
          this.errorOnFind = error;
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isFindPending = false;
        });
      }
      
      // Cache the results
      if (this.offlineOptions.enableOffline) {
        await this.cacheResults(response);
      }
      
      return response;
    } catch (error) {
      // Fallback to offline mode
      if (this.offlineOptions.enableOffline) {
        console.warn(`${this.name}: Falling back to offline mode`, error);
        return this.findOffline(params);
      }
      throw error;
    }
  };

  private async findOffline(params?: any) {
    const items = this.items.filter((item: any) => !item._deleted);
    
    // Apply basic query filters if provided
    let filtered = items;
    if (params?.query) {
      filtered = this.applyOfflineQuery(items, params.query);
    }

    // Ensure all items have id property with correct type
    const dataWithIds = filtered.slice(
      params?.query?.$skip || 0,
      (params?.query?.$skip || 0) + (params?.query?.$limit || 10)
    ).map(item => ({
      ...item,
      id: (item[this.idField] || item.id) as string | number
    }));

    const result = {
      total: filtered.length,
      limit: params?.query?.$limit || 10,
      skip: params?.query?.$skip || 0,
      data: dataWithIds
    };

    return result;
  }

  private applyOfflineQuery(items: any[], query: any): any[] {
    let result = [...items];

    // Apply filters
    Object.entries(query).forEach(([key, value]) => {
      if (key.startsWith('$')) return; // Skip operators for now

      result = result.filter(item => {
        const itemValue = item[key];
        
        if (typeof value === 'object' && value !== null) {
          // Handle operators
          if ('$in' in value) {
            return (value as any).$in.includes(itemValue);
          }
          if ('$ne' in value) {
            return itemValue !== (value as any).$ne;
          }
          // Add more operators as needed
        }
        
        return itemValue === value;
      });
    });

    // Apply sorting
    if (query.$sort) {
      const sortKeys = Object.entries(query.$sort);
      result.sort((a, b) => {
        for (const [key, direction] of sortKeys) {
          const aVal = a[key];
          const bVal = b[key];
          
          if (aVal < bVal) return direction === 1 ? -1 : 1;
          if (aVal > bVal) return direction === 1 ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }

  get = async (id: number, params: object = {}) => {
    const isOffline = !navigator.onLine || 
      (this.rootStore.connectionStore?.isOffline ?? false);
    
    // Try local first if offline
    if (isOffline && this.offlineOptions.enableOffline) {
      const cached = this.items.find((item: any) => 
        item[this.idField] === id && !item._deleted
      );
      if (cached) {
        // Ensure id property exists
        return { ...cached, id: cached[this.idField] || cached.id };
      }
    }

    try {
      // Directly call the service since we can't call super on arrow functions
      this.isGetPending = true;
      let item: any = null;

      try {
        item = await this.service.get(id, params);
        
        const index = this.items.findIndex((i: any) => i[this.idField] === id);
        if (index >= 0) {
          runInAction(() => {
            this.items[index] = item;
          });
        } else {
          runInAction(() => {
            this.items.push(item);
          });
        }
        
        runInAction(() => {
          this.errorOnGet = null;
        });
      } catch (error: any) {
        runInAction(() => {
          this.errorOnGet = error;
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isGetPending = false;
        });
      }
      
      // Cache the result
      if (this.offlineOptions.enableOffline && this.offlineStorage) {
        await this.offlineStorage.setItem(String(id), item);
      }
      
      return item;
    } catch (error) {
      // Return cached version if available
      if (this.offlineOptions.enableOffline) {
        const cached = this.items.find((item: any) => 
          item[this.idField] === id && !item._deleted
        );
        if (cached) {
          // Ensure id property exists
          return { ...cached, id: cached[this.idField] || cached.id };
        }
      }
      throw error;
    }
  };

  create = async (data: object, params: object = {}) => {
    const isOffline = !navigator.onLine || 
      (this.rootStore.connectionStore?.isOffline ?? false);
    
    if (isOffline && this.offlineOptions.enableOffline) {
      return this.createOffline(data);
    }

    try {
      // Directly call the service
      this.isCreatePending = true;
      let item: any = null;

      try {
        item = await this.service.create(data, params);
        
        runInAction(() => {
          this.items.push(item);
          this.errorOnCreate = null;
        });
      } catch (error: any) {
        runInAction(() => {
          this.errorOnCreate = error;
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isCreatePending = false;
        });
      }
      
      // Cache the result
      if (this.offlineOptions.enableOffline && this.offlineStorage) {
        await this.offlineStorage.setItem(String(item[this.idField]), item);
      }
      
      return item;
    } catch (error) {
      if (this.offlineOptions.enableOffline) {
        console.warn(`${this.name}: Creating offline`, error);
        return this.createOffline(data);
      }
      throw error;
    }
  };

  private async createOffline(data: any) {
    // Generate temporary ID
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const item = {
      ...data,
      [this.idField]: tempId,
      _isTemp: true,
      _localCreatedAt: Date.now(),
      _syncStatus: 'pending' as const
    };

    // Store locally
    if (this.offlineStorage) {
      await this.offlineStorage.setItem(tempId, item);
    }

    runInAction(() => {
      this.items.push(item);
    });

    // Queue for sync
    if (this.rootStore.offlineQueueManager) {
      await this.rootStore.offlineQueueManager.enqueue({
        storeName: this.name || '',
        method: 'create',
        data: toJS(data),
        conflictResolution: this.offlineOptions.conflictStrategy
      });
    }

    this.pendingSync.add(tempId);
    
    return item;
  }

  patch = async (id: number | null, data: object, params: object = {}) => {
    const isOffline = !navigator.onLine || 
      (this.rootStore.connectionStore?.isOffline ?? false);
    
    if (isOffline && this.offlineOptions.enableOffline) {
      return this.patchOffline(id, data);
    }

    try {
      // Directly call the service
      if (id === null) {
        throw new Error('ID cannot be null for patch');
      }
      
      this.isPatchPending = true;
      let item: any = null;

      try {
        item = await this.service.patch(id, data, params);
        
        const index = this.items.findIndex((i: any) => i[this.idField] === id);
        if (index >= 0) {
          runInAction(() => {
            this.items[index] = item;
          });
        }
        
        runInAction(() => {
          this.errorOnPatch = null;
        });
      } catch (error: any) {
        runInAction(() => {
          this.errorOnPatch = error;
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isPatchPending = false;
        });
      }
      
      // Cache the result
      if (this.offlineOptions.enableOffline && this.offlineStorage) {
        await this.offlineStorage.setItem(String(id), item);
      }
      
      return item;
    } catch (error) {
      if (this.offlineOptions.enableOffline) {
        console.warn(`${this.name}: Patching offline`, error);
        return this.patchOffline(id, data);
      }
      throw error;
    }
  };

  private async patchOffline(id: number | null, data: any) {
    if (id === null) {
      throw new Error('ID cannot be null for offline patch');
    }
    const existing = this.items.find((item: any) => 
      item[this.idField] === id
    );
    
    if (!existing) {
      throw new Error(`Item ${id} not found`);
    }

    const updated = {
      ...existing,
      ...data,
      _localUpdatedAt: Date.now(),
      _syncStatus: 'pending' as const
    };

    // Store locally
    if (this.offlineStorage) {
      await this.offlineStorage.setItem(String(id), updated);
    }

    runInAction(() => {
      const index = this.items.findIndex((item: any) => 
        item[this.idField] === id
      );
      if (index >= 0) {
        this.items[index] = updated;
      }
    });

    // Queue for sync if not a temp item
    if (!(existing as any)._isTemp && this.rootStore.offlineQueueManager) {
      await this.rootStore.offlineQueueManager.enqueue({
        storeName: this.name || '',
        method: 'patch',
        entityId: String(id),
        data: toJS(data),
        conflictResolution: this.offlineOptions.conflictStrategy
      });
    }

    this.pendingSync.add(String(id));
    
    return updated;
  }

  remove = async (id: number | null, params: object = {}) => {
    const isOffline = !navigator.onLine || 
      (this.rootStore.connectionStore?.isOffline ?? false);
    
    if (isOffline && this.offlineOptions.enableOffline) {
      return this.removeOffline(id);
    }

    try {
      // Directly call the service
      if (id === null) {
        throw new Error('ID cannot be null for remove');
      }
      
      this.isRemovePending = true;
      let result: any = null;

      try {
        result = await this.service.remove(id, params);
        
        const index = this.items.findIndex((i: any) => i[this.idField] === id);
        if (index >= 0) {
          runInAction(() => {
            this.items.splice(index, 1);
          });
        }
        
        runInAction(() => {
          this.errorOnRemove = null;
        });
      } catch (error: any) {
        runInAction(() => {
          this.errorOnRemove = error;
        });
        throw error;
      } finally {
        runInAction(() => {
          this.isRemovePending = false;
        });
      }
      
      // Remove from cache
      if (this.offlineOptions.enableOffline && this.offlineStorage) {
        await this.offlineStorage.removeItem(String(id));
      }
      
      return result;
    } catch (error) {
      if (this.offlineOptions.enableOffline) {
        console.warn(`${this.name}: Removing offline`, error);
        return this.removeOffline(id);
      }
      throw error;
    }
  };

  private async removeOffline(id: number | null) {
    if (id === null) {
      throw new Error('ID cannot be null for offline remove');
    }
    const item = this.items.find((i: any) => i[this.idField] === id);
    if (!item) throw new Error(`Item ${id} not found`);

    // If it's a temp item, just remove it
    if ((item as any)._isTemp) {
      runInAction(() => {
        const index = this.items.findIndex((i: any) => 
          i[this.idField] === id
        );
        if (index >= 0) {
          this.items.splice(index, 1);
        }
      });
      
      if (this.offlineStorage) {
        await this.offlineStorage.removeItem(String(id));
      }
      
      return item;
    }

    // Mark as deleted but keep for sync
    const deletedItem = {
      ...item,
      _deleted: true,
      _localDeletedAt: Date.now(),
      _syncStatus: 'pending' as const
    };

    if (this.offlineStorage) {
      await this.offlineStorage.setItem(String(id), deletedItem);
    }

    runInAction(() => {
      const index = this.items.findIndex((i: any) => 
        i[this.idField] === id
      );
      if (index >= 0) {
        this.items.splice(index, 1);
      }
    });

    // Queue for sync
    if (this.rootStore.offlineQueueManager) {
      await this.rootStore.offlineQueueManager.enqueue({
        storeName: this.name || '',
        method: 'remove',
        entityId: String(id),
        conflictResolution: this.offlineOptions.conflictStrategy
      });
    }

    this.pendingSync.add(String(id));
    
    return item;
  }

  async syncWithServer() {
    if (!this.offlineOptions.enableOffline || this.syncInProgress) {
      return;
    }

    // Check if online
    const isOnline = navigator.onLine && 
      !(this.rootStore.connectionStore?.isOffline ?? false);
    
    if (!isOnline) {
      console.log(`${this.name}: Cannot sync - offline`);
      return;
    }

    this.syncInProgress = true;

    try {
      // Process queued operations for this store
      if (this.rootStore.offlineQueueManager) {
        await this.rootStore.offlineQueueManager.processQueue(
          async (op: any) => {
            if (op.storeName !== this.name) return false;
            
            try {
              switch (op.method) {
                case 'create':
                  const created = await this.service.create(op.data);
                  await this.replaceTempItem(op.data, created);
                  return true;
                  
                case 'patch':
                  if (op.entityId) {
                    const patched = await this.service.patch(op.entityId, op.data);
                    await this.updateLocalItem(op.entityId, patched);
                    return true;
                  }
                  break;
                  
                case 'remove':
                  if (op.entityId) {
                    await this.service.remove(op.entityId);
                    await this.removeLocalItem(op.entityId);
                    return true;
                  }
                  break;
              }
            } catch (error: any) {
              if (error.code === 409) {
                // Conflict - handle it
                return await this.handleConflict(op, error);
              }
              throw error;
            }
            
            return false;
          },
          async (op, serverData) => {
            // Conflict handler
            const localData = this.items.find((item: any) => 
              item[this.idField] === op.entityId
            );
            
            if (!localData) return null;
            
            const resolved = await this.conflictResolver.resolve(
              localData,
              serverData,
              op.conflictResolution || this.offlineOptions.conflictStrategy || 'server'
            );
            
            await this.updateLocalItem(op.entityId!, resolved);
            return resolved;
          }
        );
      }

      // Fetch latest data from server
      await this.find();
      
      // Update sync time
      this.lastSyncTime = new Date();
      if (this.offlineStorage) {
        await this.offlineStorage.setItem('_lastSync', this.lastSyncTime.toISOString());
      }
      
      // Clear pending sync items
      this.pendingSync.clear();
      
    } catch (error) {
      console.error(`${this.name}: Sync failed`, error);
      throw error;
    } finally {
      this.syncInProgress = false;
    }
  }

  private async replaceTempItem(tempData: any, serverItem: any) {
    // Find temp item that matches the data
    const tempItem = this.items.find((item: any) => 
      item._isTemp && this.isSameItem(item, tempData)
    );
    
    if (!tempItem) return;
    
    const tempId = (tempItem as any)[this.idField];
    
    // Remove temp item
    if (this.offlineStorage) {
      await this.offlineStorage.removeItem(String(tempId));
    }
    
    // Add server item
    if (this.offlineStorage) {
      await this.offlineStorage.setItem(
        String(serverItem[this.idField]), 
        serverItem
      );
    }
    
    runInAction(() => {
      const index = this.items.findIndex((item: any) => 
        item[this.idField] === tempId
      );
      if (index >= 0) {
        this.items[index] = serverItem;
      }
    });
  }

  private async updateLocalItem(id: string, item: any) {
    if (this.offlineStorage) {
      await this.offlineStorage.setItem(id, item);
    }
    
    runInAction(() => {
      const index = this.items.findIndex((i: any) => 
        i[this.idField] === id
      );
      if (index >= 0) {
        this.items[index] = item;
      }
    });
  }

  private async removeLocalItem(id: string) {
    if (this.offlineStorage) {
      await this.offlineStorage.removeItem(id);
    }
    
    runInAction(() => {
      const index = this.items.findIndex((i: any) => 
        i[this.idField] === id
      );
      if (index >= 0) {
        this.items.splice(index, 1);
      }
    });
  }

  private async handleConflict(operation: any, error: any): Promise<boolean> {
    const serverData = error.serverData;
    const localData = this.items.find((item: any) => 
      item[this.idField] === operation.entityId
    );
    
    if (!localData || !serverData) return false;
    
    const resolved = await this.conflictResolver.resolve(
      localData,
      serverData,
      operation.conflictResolution || this.offlineOptions.conflictStrategy || 'server'
    );
    
    await this.updateLocalItem(operation.entityId, resolved);
    
    return true;
  }

  private isSameItem(item1: any, item2: any): boolean {
    // Compare key fields to determine if it's the same item
    // This is a simple implementation - override for specific needs
    const keys = ['name', 'title', 'description', 'type'];
    
    for (const key of keys) {
      if (key in item1 && key in item2) {
        if (item1[key] === item2[key]) {
          return true;
        }
      }
    }
    
    return false;
  }

  private async cacheResults(response: any) {
    if (!this.offlineStorage) return;
    
    const data = Array.isArray(response) ? response : 
      (response.data || response.result?.data || []);
    
    for (const item of data) {
      const id = item[this.idField];
      if (id) {
        await this.offlineStorage.setItem(String(id), item);
      }
    }
  }

  async clearOfflineData() {
    if (this.offlineStorage) {
      await this.offlineStorage.clear();
    }
    
    this.pendingSync.clear();
    this.lastSyncTime = null;
  }

  get hasPendingSync() {
    return this.pendingSync.size > 0;
  }

  get entities() {
    // Override to filter out deleted items
    return this.items.filter((item: any) => !item._deleted);
  }
}